/**
 * Componente para gerenciar Overflows
 * VigosCloud - Message Context System
 * 
 * Gerencia overflows (transbordos) para contextos de mensagem
 */

import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faExchangeAlt, 
  faPlus, 
  faTrash,
  faVolumeUp,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';

import './MessageComponents.css';
import AudioSelectorUploads from '../../Audio/AudioSelectorUploads';
import StasisFallbackInputs from '../../shared/StasisFallbackInputs';

function OverflowManager({
  overflows = [],
  onChange,
  availableEndpoints = [],
  availableQueues = [],
  availableContexts = [],
  availableTrunks = [],
  tenant,
  disabled = false,
  hasWhatsAppIntegration = false,
  userRole = ''
}) {

  const addOverflow = () => {
    const newOverflows = [...overflows, {
      timeout_seconds: 20,
      destination_type: 'endpoint',
      destination_overflow: '',
      audio_enabled: false,
      audio_file: '',
      audio_type: 'message',
      audio_accountcode: tenant,
      hangup_after_audio: false,
      order_priority: overflows.length + 1
    }];
    if (onChange) {
      onChange(newOverflows);
    }
  };

  const removeOverflow = (index) => {
    const newOverflows = overflows.filter((_, i) => i !== index);
    // Reordenar order_priority
    newOverflows.forEach((overflow, i) => {
      overflow.order_priority = i + 1;
    });
    if (onChange) {
      onChange(newOverflows);
    }
  };

  const updateOverflow = (index, field, value) => {
    const newOverflows = [...overflows];
    newOverflows[index] = {
      ...newOverflows[index],
      [field]: value
    };

    if (field === 'destination_type' && ['hangup', 'cycle', 'whatsapp'].includes(value)) {
      newOverflows[index].destination_overflow = '';
      newOverflows[index].trunk_name = '';
    }

    if (onChange) {
      onChange(newOverflows);
    }
  };

  // Atualiza múltiplos campos de um overflow numa única chamada de onChange.
  // Necessário porque chamadas sequenciais de updateOverflow leem a mesma
  // referência stale de `overflows` (closure), causando perda de campos
  // quando o parent recebe os arrays via setValues sem updater funcional.
  const updateOverflowFields = (index, fields) => {
    const newOverflows = [...overflows];
    newOverflows[index] = {
      ...newOverflows[index],
      ...fields
    };
    if (onChange) {
      onChange(newOverflows);
    }
  };

  const getAvailableDestinationTypes = () => {
    const types = [
      { value: 'endpoint', label: 'Ramal' },
      { value: 'queue', label: 'Fila' },
      { value: 'external', label: 'Externo' },
      { value: 'disa', label: 'DISA' },
      {
        value: 'whatsapp',
        label: hasWhatsAppIntegration ? 'WhatsApp' : 'WhatsApp (sem integração)',
        disabled: !hasWhatsAppIntegration
      },
      { value: 'cycle', label: 'Ciclo (Reiniciar)' },
      { value: 'hangup', label: 'Desligar' }
    ];
    if (userRole === 'root') {
      types.push({ value: 'stasis', label: 'Agent IA' });
    }
    return types;
  };

  const renderDestinationInput = (overflow, index) => {
    switch (overflow.destination_type) {
      case 'endpoint':
        return (
          <input
            className="input is-small"
            type="text"
            placeholder="Ex: 1000"
            value={overflow.destination_overflow || ''}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 4);
              updateOverflow(index, 'destination_overflow', value);
            }}
            maxLength={4}
            disabled={disabled}
          />
        );
      
      case 'queue':
        return (
          <div className="select is-small is-fullwidth">
            <select
              value={overflow.destination_overflow || ''}
              onChange={(e) => updateOverflow(index, 'destination_overflow', e.target.value)}
              disabled={disabled}
            >
              <option value="">Selecione uma fila</option>
              {availableQueues.map((queue) => (
                <option key={queue.namequeue || queue.name} value={queue.namequeue || queue.name}>
                  {queue.namequeue || queue.name} - {queue.displayname || queue.namequeue || queue.name}
                </option>
              ))}
            </select>
          </div>
        );
      
      case 'disa':
        return (
          <div className="select is-small is-fullwidth">
            <select
              value={overflow.destination_overflow || ''}
              onChange={(e) => updateOverflow(index, 'destination_overflow', e.target.value)}
              disabled={disabled}
            >
              <option value="">Selecione um contexto</option>
              {availableContexts.filter(context => {
                const contextName = (context.name || context.context_name || '').toLowerCase();
                return contextName.includes('ura-') || contextName.includes('msg-');
              }).map((context) => (
                <option key={context.name || context.context_name} value={context.name || context.context_name}>
                  {context.label || context.name || context.context_name}
                </option>
              ))}
            </select>
          </div>
        );
      
      case 'external':
        return (
          <>
            <input
              className="input is-small mb-2"
              type="text"
              placeholder="Ex: 1134567890"
              value={overflow.destination_overflow || ''}
              onChange={(e) => updateOverflow(index, 'destination_overflow', e.target.value)}
              disabled={disabled}
            />
            <div className="field mt-2">
              <label className="label is-small">Tronco *</label>
              <div className="select is-small is-fullwidth">
                <select
                  value={overflow.trunk_name || ''}
                  onChange={(e) => updateOverflow(index, 'trunk_name', e.target.value)}
                  disabled={disabled}
                >
                  <option value="">Selecione o tronco</option>
                  {availableTrunks.map((trunk) => (
                    <option key={trunk.name} value={trunk.name}>
                      {trunk.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </>
        );
      
      case 'whatsapp':
        return (
          <input
            className="input is-small"
            type="text"
            value="WhatsApp AGI"
            disabled
            style={{ backgroundColor: '#f5f5f5' }}
          />
        );
      
      case 'cycle':
        return (
          <input
            className="input is-small"
            type="text"
            value="CYCLE_RESTART"
            disabled
            style={{ backgroundColor: '#f5f5f5' }}
          />
        );
      
      case 'hangup':
        return (
          <input
            className="input is-small"
            type="text"
            value="Desligar chamada"
            disabled
            style={{ backgroundColor: '#f5f5f5' }}
          />
        );

      case 'stasis':
        return (
          <>
            <input
              className="input is-small mb-2"
              type="text"
              value="voice-gateway-ai (fixo)"
              disabled
              style={{ backgroundColor: '#f5f5f5' }}
            />
            <StasisFallbackInputs
              fallbackType={overflow.stasis_fallback_type || ''}
              fallbackDestination={overflow.stasis_fallback_destination || ''}
              onChange={(field, value) => updateOverflow(index, field, value)}
              availableEndpoints={availableEndpoints}
              availableQueues={availableQueues}
              availableContexts={availableContexts}
            />
          </>
        );

      default:
        return (
          <input
            className="input is-small"
            type="text"
            placeholder="Destino"
            value={overflow.destination_overflow || ''}
            onChange={(e) => updateOverflow(index, 'destination_overflow', e.target.value)}
            disabled={disabled}
          />
        );
    }
  };

  return (
    <div className="form-section-card">
      <div className="level">
        <div className="level-left">
          <div className="level-item">
            <h3 className="title is-5">
              <FontAwesomeIcon icon={faExchangeAlt} className="mr-2" />
              Transbordos (Overflows)
            </h3>
          </div>
        </div>
        <div className="level-right">
          <div className="level-item">
            <button
              type="button"
              className="button is-primary is-small"
              onClick={addOverflow}
              disabled={disabled}
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Adicionar Transbordo
            </button>
          </div>
        </div>
      </div>

      {overflows.length === 0 ? (
        <div className="notification is-light">
          <p className="has-text-centered">
            <strong>Nenhum transbordo configurado</strong>
          </p>
          <p className="has-text-centered">
            Clique em "Adicionar Transbordo" para criar uma cadeia de encaminhamentos.
          </p>
        </div>
      ) : (
        overflows.map((overflow, index) => (
          <div key={index} className="box">
            <div className="level">
              <div className="level-left">
                <div className="level-item">
                  <h4 className="subtitle is-6">Transbordo #{index + 1}</h4>
                </div>
              </div>
              <div className="level-right">
                <div className="level-item">
                  <button
                    type="button"
                    className="button is-small is-danger"
                    onClick={() => removeOverflow(index)}
                    disabled={disabled}
                    title="Remover transbordo"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
            </div>

            <div className="columns">
              <div className="column is-3">
                <div className="field">
                  <label className="label is-small">Timeout (segundos) *</label>
                  <div className="control">
                    <input
                      className="input is-small"
                      type="number"
                      min="1"
                      max="300"
                      value={overflow.timeout_seconds || 20}
                      onChange={(e) => updateOverflow(index, 'timeout_seconds', parseInt(e.target.value) || 20)}
                      disabled={disabled}
                    />
                  </div>
                  <p className="help is-size-7">Tempo antes do transbordo</p>
                </div>
              </div>

              <div className="column is-3">
                <div className="field">
                  <label className="label is-small">Tipo de Destino do Transbordo *</label>
                  <div className="control">
                    <div className="select is-small is-fullwidth">
                      <select
                        value={overflow.destination_type || 'endpoint'}
                        onChange={(e) => updateOverflow(index, 'destination_type', e.target.value)}
                        disabled={disabled}
                      >
                        {getAvailableDestinationTypes().map(type => (
                          <option 
                            key={type.value} 
                            value={type.value}
                            disabled={type.disabled}
                          >
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="column is-6">
                <div className="field">
                  <label className="label is-small">Destino do Transbordo *</label>
                  <div className="control">
                    {renderDestinationInput(overflow, index)}
                  </div>
                </div>
              </div>
            </div>

            <div className="field">
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={overflow.audio_enabled || false}
                  onChange={(e) => updateOverflow(index, 'audio_enabled', e.target.checked)}
                  disabled={disabled}
                />
                <span className="ml-2">
                  <FontAwesomeIcon icon={faVolumeUp} className="mr-2" />
                  Reproduzir áudio antes deste transbordo
                </span>
              </label>
            </div>

            {overflow.audio_enabled && (
              <div>
                <div className="notification is-info is-light mb-3">
                  <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />
                  Selecione um arquivo de áudio que será reproduzido antes deste transbordo.
                </div>

                <AudioSelectorUploads
                  tenant={tenant}
                  selectedFile={overflow.audio_file}
                  selectedType={overflow.audio_type || 'message'}
                  onAudioChange={(audioData) => {
                    updateOverflowFields(index, {
                      audio_file: audioData.file,
                      audio_type: audioData.type,
                      audio_accountcode: audioData.accountcode
                    });
                  }}
                  disabled={disabled}
                />
                
                <div className="field mt-3">
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={overflow.hangup_after_audio || false}
                      onChange={(e) => updateOverflow(index, 'hangup_after_audio', e.target.checked)}
                      disabled={disabled}
                    />
                    <span className="ml-2">Desligar após reproduzir áudio</span>
                  </label>
                  <p className="help is-size-7">Se marcado, não executa o destino configurado</p>
                </div>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

export default OverflowManager;

